import {
  Group,
  type GroupProps,
  lighten,
  type MantineSize,
  Paper,
  type PaperProps,
  Skeleton,
  Stack,
  Text,
  type TextProps,
  useMantineTheme,
} from '@mantine/core';
import clsx from 'clsx';
import { type CSSProperties, type ReactNode, useState } from 'react';
import type { IconBaseProps, IconType } from 'react-icons';
import classes from './MetricsDisplay.module.css';

type MetricItem = {
  label: ReactNode;
  value: ReactNode;
  ValueIcon?: IconType;
  ValueIconProps?: IconBaseProps;
  id?: string;
  color?: string;
};

type MetricsDisplayProps = {
  metrics: MetricItem[];
  labelProps?: TextProps;
  valueProps?: TextProps;
  containerProps?: GroupProps;
  showDividerAfterFirst?: boolean;
  isLoading?: boolean;
  onMetricsChange?: (metricId: string | null) => void;
  paperProps?: Omit<PaperProps, 'children' | 'onClick'>;
  noneText?: string;
  radius?: MantineSize;
};

function MetricsDisplay({
  metrics,
  labelProps,
  valueProps,
  containerProps,
  showDividerAfterFirst = false,
  isLoading = false,
  onMetricsChange,
  paperProps,
  noneText = 'None',
  radius = 'md',
}: MetricsDisplayProps) {
  const count = metrics.length || 4;
  const [activeMetricInnerState, setActiveMetricInnerState] = useState<
    string | null
  >(null);

  const mantineTheme = useMantineTheme();

  const getSelectedStyle = (
    isSelected: boolean,
    color?: string,
  ): CSSProperties => {
    if (!isSelected || !color || !mantineTheme.colors[color]) {
      return {
        cursor: onMetricsChange ? 'pointer' : 'default',
      };
    }

    const colorValue = mantineTheme.colors[color];
    return {
      backgroundImage: `linear-gradient(216deg,${lighten(colorValue[6] as string, 0.9)} 10%, rgba(255, 255, 255, 1) 80%)`,
      border: `1px solid ${colorValue[4]}`,
      cursor: 'pointer',
    };
  };

  if (isLoading) {
    return (
      <Group gap="md" mih={50} {...containerProps}>
        {Array.from({ length: count }, (_, index) => (
          <Stack key={index} gap="xs">
            <Skeleton height={10} width={135} />
            <Skeleton height={12} width={100} />
          </Stack>
        ))}
      </Group>
    );
  }

  return (
    <Group
      gap={0}
      mih={50}
      data-testid="metricsDisplay"
      className={classes.root}
      align="stretch"
      style={{
        '--metric-radius': `var(--mantine-radius-${radius})`,
      }}
      {...containerProps}
    >
      {metrics.map((metric, index) => {
        const value =
          typeof metric.value === 'string' &&
          metric.value.toLowerCase() === 'none'
            ? null
            : metric.value;

        const isSelected = Boolean(
          onMetricsChange &&
            metric.id !== undefined &&
            metric.id === activeMetricInnerState,
        );

        return (
          <Paper
            key={metric.id ?? index}
            bg="accent.1"
            withBorder
            py={6}
            px="md"
            component={Stack}
            radius={0}
            gap={4}
            justify="center"
            data-testid="metricsDisplayItem"
            {...paperProps}
            className={clsx(
              classes.metric,
              showDividerAfterFirst && index === 0 && classes.isolatedFirst,
              showDividerAfterFirst &&
                index === 1 &&
                classes.nextOfIsolatedFirst,
              paperProps?.className,
            )}
            style={{
              ...getSelectedStyle(isSelected, metric.color),
              ...paperProps?.style,
            }}
            onClick={() => {
              if (!onMetricsChange || metric.id === undefined) {
                return;
              }

              if (metric.id === activeMetricInnerState) {
                setActiveMetricInnerState(null);
                onMetricsChange(null);
                return;
              }

              setActiveMetricInnerState(metric.id);
              onMetricsChange(metric.id);
            }}
          >
            <Text
              c={metric.color ?? 'dimmed'}
              tt="uppercase"
              fw={600}
              size="10px"
              {...labelProps}
            >
              {metric.label}
            </Text>
            <Group gap={4} align="center">
              {metric.ValueIcon && (
                <metric.ValueIcon {...metric.ValueIconProps} />
              )}
              <Text
                component="div"
                size="xs"
                c={value ? undefined : 'dimmed'}
                {...valueProps}
              >
                {value ?? noneText}
              </Text>
            </Group>
          </Paper>
        );
      })}
    </Group>
  );
}

export { type MetricItem, MetricsDisplay, type MetricsDisplayProps };
